import { Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
// Required for MSAL
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionRequiredAuthError, InteractionStatus, PublicClientApplication, RedirectRequest } from '@azure/msal-browser';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { delay, filter, map, takeUntil, tap } from 'rxjs/operators';

import { ColorModeService } from '@coreui/angular';
import { IconSetService } from '@coreui/icons-angular';
import { iconSubset } from './icons/icon-subset';
import { Subject } from 'rxjs';
import { LoadingService } from './services/loading.service';
import { environment } from '../environments/environment';
import { PopupRequest } from '@azure/msal-browser';


@Component({
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
  `,
  standalone: true,
  imports: [RouterOutlet]
})
export class AppComponent implements OnInit {
  title = 'ROS AI Agent';
  loginDisplay = false;
  tokenExpiration: string = '';
  private readonly _destroying$ = new Subject<void>();
  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);

  readonly #colorModeService = inject(ColorModeService);
  readonly #iconSetService = inject(IconSetService);


  isLoading = false;

  constructor(
    // @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, // Temporarily commented out
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private loadingService: LoadingService,
    private routerInstance: Router) // Renamed to avoid conflict with this.#router if #router is used in constructor body later
    {
    this.#titleService.setTitle(this.title);
    // iconSet singleton
    this.#iconSetService.icons = { ...iconSubset };
    this.#colorModeService.localStorageItemName.set('email-response-system-theme-default');
    this.#colorModeService.eventName.set('ColorSchemeChange');
    this.loadingService.loading$.subscribe((loading) => {
      this.isLoading = loading;
    });

  }


  ngOnInit(): void {
    console.log('MSAL instance in ngOnInit (before initialize):', this.msalService.instance);

    this.msalService.instance.initialize()
    .then(() => {
      this.checkAuthStatus();
      this.setupAuthCallbackListener();
      this.handleSilentRenew();
    })
    .catch(error => console.error('MSAL init failed:', error));

    // Handle the redirect response and navigate to the correct route
    this.msalBroadcastService.inProgress$
        .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });

      // Used for storing and displaying token expiration
      this.msalBroadcastService.msalSubject$.pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS)).subscribe(msg => {
      this.tokenExpiration=  (msg.payload as any).expiresOn;
      localStorage.setItem('tokenExpiration', this.tokenExpiration);
    });

    this.#activatedRoute.queryParams
      .pipe(
        delay(1),
        map(params => <string>params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
        filter(theme => ['dark', 'light', 'auto'].includes(theme)),
        tap(theme => {
          this.#colorModeService.colorMode.set(theme);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

async checkAuthStatus() {
  console.log('[Child] checkAuthStatus called');

  const accounts = this.msalService.instance.getAllAccounts();
  console.log('[Child] Accounts:', accounts);


  if (accounts.length === 0) {

    console.log('[Child] Sending LOGIN_REQUIRED');
    window.parent.postMessage(
      { type: 'LOGIN_REQUIRED' },
      'https://rosnyc.com' // Consider using environment.parentOrigin if available
    );
    this.sendLoginRequiredEvent();
  }
}

   handleSilentRenew() {
    this.msalService.instance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        // window.parent.postMessage('TOKEN_RENEWED', 'https://rosnyc.com');
      }
    });
  }

  async sendLoginRequiredEvent() {
    console.log('sendLoginRequiredEvent triggered');

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    const usePopup = isSafari || isMobile;

    const loginRequest: RedirectRequest = {
      scopes: ['openid', 'profile', 'User.Read'],
      redirectUri: `${environment.redirectUri}`,
      authority: environment.authority
    };
    const loginRequestPopup: PopupRequest = {
      scopes: ['openid', 'profile', 'user.read'],
      // redirectUri: `https://rosnyc.com/admin/index.php?action=ers-system`,
      redirectUri: environment.redirectUri,
      authority: environment.authority
    };

    try {
      const pca = this.msalService.instance;
      console.log('MSAL instance (pca):', pca);

      if (usePopup) {
        console.log('Using popup login');

        const result = await pca.loginPopup(loginRequestPopup).then((response) => {
          console.log('Popup login success:', response);
          localStorage.setItem('accountInfo', JSON.stringify(result));
          window.parent.postMessage('AUTH_COMPLETE', 'https://rosnyc.com');
        }).catch((error) => {
          console.error('Popup login failed:', error);
          window.parent.postMessage(
            { type: 'LOGIN_URL_GENERATION_FAILED', error: error.message },
            'https://rosnyc.com'
          );
        });

      } else {
        console.log('Using redirect login');
        window.parent.postMessage(
          { type: 'LOGIN_REQUIRED' },
          'https://rosnyc.com'
        );
      }

    } catch (error: any) {
      console.error('Login process failed:', error);
      window.parent.postMessage(
        { type: 'LOGIN_URL_GENERATION_FAILED', error: error.message || 'Unknown error' },
        'https://rosnyc.com'
      );
    }
  }


  private setupAuthCallbackListener() {
    this.msalService.handleRedirectObservable().subscribe({
      complete: () => {
        console.log("[CHILD] sending auth complete to parent")
        window.parent.postMessage('AUTH_COMPLETE', 'https://rosnyc.com');
      },
      error: (error) => console.error('Authentication failed:', error)
    });
  }

  // If the user is logged in, present the user with a "logged in" experience
  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
